import React, { useEffect, useState } from 'react';
import { 
  X, 
  Download, 
  FileText, 
  Loader2, 
  ShieldCheck, 
  AlertCircle, 
  FileDigit, 
  Database,
  Maximize2,
  ExternalLink
} from 'lucide-react';

const FilePreviewModal = ({ isOpen, file, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [isFullScreen, setIsFullScreen] = useState(false);

  useEffect(() => {
    if (isOpen && file) {
      setLoading(true);
      const fileName = file.document_name || "document";
      const fileExt = fileName.split('.').pop().toLowerCase();
      
      const officeExts = ['xlsx', 'xls', 'docx', 'doc', 'pptx', 'ppt'];
      const isOffice = officeExts.includes(fileExt);

      if (isOffice) {
        // وقت انتظار بسيط لضمان تحميل Iframe ميكروسوفت
        const timer = setTimeout(() => {
          setLoading(false);
        }, 2000);
        return () => clearTimeout(timer);
      } else {
        setLoading(false);
      }
    }
  }, [isOpen, file]);

  if (!isOpen || !file) return null;

  const fileName = file.document_name || "document";
  const fileExt = fileName.split('.').pop().toLowerCase();
  const isOffice = ['xlsx', 'xls', 'docx', 'doc', 'pptx', 'ppt'].includes(fileExt);
  const isImage = file.file_type?.includes('image');
  const isPDF = file.file_type?.includes('pdf');
  // ... داخل المكون بعد تعريف الـ constants الأخرى
  const isVideo = file.file_type?.includes('video');
  const isAudio = file.file_type?.includes('audio');

  // رابط ميكروسوفت أوفيس (Microsoft Office Online Viewer)
  // نستخدم encodeURI لضمان تحويل المسافات لـ %20 كما نجح في الـ curl
  const directFileUrl = encodeURI(`https://app.teknora-eam.com/raw-attachments/${file.file_path}`);
  const microsoftViewUrl = `https://view.officeapps.live.com/op/view.aspx?src=${directFileUrl}`;
  
  // رابط الباكيند للتحميل
  const backendDownloadUrl = `/api/attachments/view?name=${encodeURIComponent(file.file_path)}`;

  // تبديل حالة الفول سكرين
  const toggleFullScreen = () => setIsFullScreen(!isFullScreen);

  return (
    <div className={`fixed inset-0 z-[6000] flex items-center justify-center bg-black/95 backdrop-blur-md animate-in fade-in duration-300 ${isFullScreen ? 'p-0' : 'p-2 md:p-6'}`}>
      <div className={`relative flex flex-col bg-white shadow-2xl overflow-hidden transition-all duration-300 ${isFullScreen ? 'w-full h-full rounded-none' : 'w-[96vw] h-[94vh] rounded-sm border border-white/20'}`}>
        
        {/* --- Header Toolbar --- */}
        <div className="bg-[#2b579a] text-white p-3 flex justify-between items-center shadow-lg z-20 font-bold uppercase">
          <div className="flex items-center gap-4">
            <div className="bg-white/20 p-2 rounded shadow-inner border border-white/10 hidden md:block">
                {fileExt.includes('xl') ? <FileDigit size={18}/> : <FileText size={18}/>}
            </div>
            <div className="flex flex-col tracking-tighter text-left leading-none">
                <span className="font-black text-[11px] leading-none tracking-widest mb-1">{fileName}</span>
                <span className="text-[8px] text-blue-100 font-bold flex items-center gap-1 uppercase tracking-widest opacity-80">
                    <ShieldCheck size={10} className="text-green-300"/> Powered by Microsoft Cloud Rendering
                </span>
            </div>
          </div>

          <div className="flex items-center gap-2 md:gap-4">
            {/* زر فتح في نافذة مستقلة */}
            {isOffice && (
              <a 
                href={microsoftViewUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-2 hover:bg-white/10 rounded-full transition-colors title='Open in new tab'"
              >
                <ExternalLink size={18}/>
              </a>
            )}
            
            {/* زر الفول سكرين */}
            <button 
              onClick={toggleFullScreen}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
              title={isFullScreen ? "Exit Full Screen" : "Full Screen"}
            >
              <Maximize2 size={18} className={isFullScreen ? "rotate-180" : ""}/>
            </button>

            {/* زر التحميل */}
            <a 
              href={backendDownloadUrl} 
              download={fileName}
              className="hidden lg:flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 px-4 py-1.5 rounded text-[9px] font-black uppercase transition-all shadow-md"
            >
              <Download size={14}/> Download
            </a>

            {/* زر الإغلاق */}
            <button 
              onClick={onClose} 
              className="bg-red-600 hover:bg-red-700 text-white p-1 rounded-full transition-all shadow-lg ml-2"
            >
              <X size={24}/>
            </button>
          </div>
        </div>

        {/* --- Workspace Area --- */}
        <div className="flex-1 bg-gray-100 relative overflow-hidden">
          
          {loading && (
            <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-white/95">
                <Loader2 className="animate-spin text-[#2b579a] mb-4" size={48}/>
                <span className="font-black text-[10px] text-[#2b579a] uppercase tracking-[0.3em] animate-pulse px-6 text-center">
                  Connecting to Microsoft Office Online Cloud...
                </span>
            </div>
          )}

          <div className="h-full w-full bg-white">
            {isOffice ? (
                /* Microsoft Office Viewer Iframe */
                <iframe 
                  src={microsoftViewUrl} 
                  className="w-full h-full border-none shadow-inner" 
                  title="office-viewer"
                  frameBorder="0"
                />
            ) : isImage ? (
                <div className="h-full w-full flex items-center justify-center p-8 bg-[#1e1e1e]">
                    <img 
                      src={backendDownloadUrl} 
                      className="max-w-full max-h-full object-contain shadow-2xl border border-white/5" 
                      alt="preview" 
                    />
                </div>
            ) : isPDF ? (
                <iframe 
                  src={`${backendDownloadUrl}#toolbar=0&navpanes=0`} 
                  className="w-full h-full border-none shadow-inner" 
                  title="pdf-viewer" 
                />
            ) : isVideo ? (
                    /* --- مشغل الفيديو --- */
                    <div className="h-full w-full flex items-center justify-center bg-black">
                      <video 
                        controls 
                        autoPlay 
                        className="max-w-full max-h-full shadow-2xl"
                        src={backendDownloadUrl}
                      >
                        Your browser does not support the video tag.
                      </video>
                    </div>
            ) : isAudio ? (
                /* --- مشغل الصوت --- */
                <div className="h-full w-full flex flex-col items-center justify-center bg-slate-50 gap-8">
                  <div className="bg-[#2b579a] p-8 rounded-full shadow-2xl animate-pulse">
                    <Database size={48} className="text-white" />
                  </div>
                  <audio 
                    controls 
                    autoPlay 
                    className="w-full max-w-md shadow-lg"
                    src={backendDownloadUrl}
                  >
                    Your browser does not support the audio element.
                  </audio>
                  <span className="text-[10px] font-black text-[#2b579a] uppercase tracking-widest italic">
                    Playing Audio Stream: {fileName}
                  </span>
                </div>                
            ) : (
                <div className="h-full flex flex-col items-center justify-center text-gray-400 gap-6 uppercase bg-white px-6">
                    <FileText size={80} className="opacity-10" strokeWidth={1}/>
                    <div className="text-center">
                        <p className="font-black text-[11px] tracking-widest text-gray-600 mb-2">Advanced Preview Unsupported</p>
                        <p className="text-[8px] text-gray-400">Extension: {fileExt}</p>
                    </div>
                    <a 
                      href={backendDownloadUrl} 
                      download 
                      className="bg-[#2b579a] text-white px-12 py-4 rounded-sm font-black text-[10px] tracking-widest shadow-xl"
                    >
                      Download Document
                    </a>
                </div>
            )}
          </div>
        </div>

        {/* --- Footer Status Bar --- */}
        {!isFullScreen && (
          <div className="bg-gray-50 border-t p-2 flex justify-between items-center px-6 transition-all">
              <div className="flex items-center gap-2 text-[8px] font-bold text-gray-400 uppercase tracking-tighter">
                  <Database size={10}/> 
                  <span>Storage Resource: {file.file_path}</span>
              </div>
              <div className="hidden md:flex items-center gap-4 uppercase leading-none">
                  <span className="text-[8px] font-bold text-blue-600 tracking-widest">
                    ● Microsoft Cloud Rendering Stream Active
                  </span>
              </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FilePreviewModal;